// src/components/GoldenShell.jsx — ROUND 2 version (replaces the Round 1 file).
// Change: the basket drawer now shows the SERVER-calculated basket (calc from useCart): free gift lines
// (free tealight, free Little Buddha), "3rd free" discount, nudges and free-shipping progress. Plus Remove,
// real WhatsApp links (only when a real number is set) and the free-shipping threshold from settings.
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useCart } from "../lib/cart";
import { http } from "../lib/api";
import { waNumber } from "../lib/utils";
import { useCatalog, useSeason } from "../lib/goldenHourData";
import { Announcement, Header, Footer, BasketDrawer, useReveal, usePageLoad, inr } from "./GoldenHour";
import WhatsAppFab from "./WhatsAppFab";

export default function GoldenShell({ settings, children }) {
  const loc = useLocation();
  const { cart, calc, busy, count, open, setOpen, setQty, removeLine, setDiscountCode, setAddon } = useCart();
  const cat = useCatalog();
  useReveal(loc.pathname);
  usePageLoad(loc.pathname);
  useEffect(() => { window.scrollTo(0, 0); }, [loc.pathname]);

  const isCheckout = loc.pathname.startsWith("/checkout");
  const isProduct = loc.pathname.startsWith("/product/");
  const waNum = waNumber(settings?.whatsapp_number);
  const waHref = waNum ? `https://wa.me/${waNum}` : null;
  const cutoff = (settings?.festive_cutoffs || []).find((c) => c.name === "Diwali");
  const afterCutoff = cutoff && new Date(`${cutoff.cutoff_date}T23:59:59`) < new Date();
  const showAnnounce = !isCheckout && !afterCutoff;
  const cutLabel = settings?.retail_cutoff_label || "1 Nov";
  const season = useSeason();
  const onlineOn = settings?.online_payment_enabled === true;

  // server lines when available (includes FREE-TEALIGHT / FREE-BUDDHA gift lines), client lines while it loads
  // ADDON-GIFTBAG / ADDON-NAMETAG lines are shown as the add-on ticks below the lines, not as removable lines
  const src = (calc && calc.lines ? calc.lines : cart.lines).filter((l) => !String(l.sku).startsWith("ADDON-"));
  const lines = src.map((l, i) => {
    const gift = !!l.is_gift;
    const it = cat.bySku(l.linked_sku || l.sku);
    const unit = l.unit_price != null ? l.unit_price : (it?.price || 0);
    const pieces = (l.component_colours || []).map((c) => c.colour).filter(Boolean);
    return {
      key: `${l.sku}-${l.colour}-${i}`, gift,
      name: gift ? (l.name || "Free gift").replace(/\s*\(gift\)\s*$/i, "") : (it?.name || l.name || l.sku),
      image: it?.image || null, focal: it?.focal, icon: gift ? "flame" : "gift",
      meta: [l.colour, ...pieces].filter(Boolean).join(" · "),
      qty: l.qty, lineTotal: l.line_total != null ? l.line_total : unit * l.qty,
      onInc: gift ? null : () => setQty(l.sku, l.colour, l.qty + 1),
      onDec: gift ? null : () => (l.qty > 1 ? setQty(l.sku, l.colour, l.qty - 1) : removeLine(l.sku, l.colour)),
      onRemove: gift ? null : () => removeLine(l.sku, l.colour),
    };
  });
  const threshold = calc?.free_ship_threshold || settings?.free_shipping_threshold || 699;
  const subtotal = calc ? calc.subtotal : lines.reduce((a, b) => a + b.lineTotal, 0);
  const bogo = calc?.bogo_discount || 0;
  const codeOff = calc?.code_discount || 0;
  const after = subtotal - bogo - codeOff;
  const toFree = calc ? calc.amount_to_free_shipping : Math.max(0, threshold - subtotal);
  const pct = subtotal <= 0 ? 0 : Math.min(100, Math.round(((threshold - toFree) / threshold) * 100));
  const discountLines = [
    bogo > 0 ? { label: "3rd holder or keychain free", amount: bogo } : null,
    codeOff > 0 ? { label: `Code ${calc.applied_code || ""}`.trim(), amount: codeOff } : null,
  ].filter(Boolean);
  const nudges = (calc?.nudges || []).map((n) => (typeof n === "string" ? n : n.text)).filter(Boolean);
  // discount code (WELCOME100, Gift Finder vouchers, referral codes) — the old drawer was the only place to enter one
  const typed = (cart.discount_code || "").trim();
  const code = {
    value: cart.discount_code || "",
    onChange: setDiscountCode,
    ok: !!(calc && calc.applied_code),
    note: !typed ? "" : busy ? "Checking…" : calc && calc.applied_code ? `${calc.applied_code} applied` : "This code doesn't apply to this basket",
  };
  const addons = [
    { key: "gift-bag", label: "Add a gift bag", price: 149, checked: cart.add_gift_bag, onChange: (v) => setAddon("add_gift_bag", v) },
    { key: "name-tag", label: "Add a name tag", price: 99, checked: cart.add_name_tag, onChange: (v) => setAddon("add_name_tag", v) },
  ];

  return (
    <div className="lo-site">
      {showAnnounce ? (
        <Announcement>
          <><b>Diwali delivery:</b> order by {cutLabel}<span className="lo-desk"><span className="lo-sep"> · </span>Free gift-wrap and a handwritten note on every order</span></>
        </Announcement>
      ) : null}
      <Header minimal={isCheckout} pathname={loc.pathname} cartCount={count} onCartOpen={() => setOpen(true)} hotNote={season.retailOn ? `Order by ${cutLabel}` : ""} festive={season.diwaliOn} whatsappHref={waHref} />
      <main>{children}</main>
      {!isCheckout ? (
        <Footer whatsappHref={waHref} settings={settings} onlineOn={onlineOn} onSubscribe={(phone) => http.post("/newsletter/subscribe", { phone: String(phone || "").replace(/\D/g, ""), source: "footer" })} />
      ) : null}
      <BasketDrawer open={open} onClose={() => setOpen(false)} lines={lines} subtotalText={inr(after)} toFreeShipping={toFree} freeShipPct={pct} nudges={nudges} discountLines={discountLines} code={code} addons={addons} onlineOn={onlineOn} />
      {waNum && !isProduct && !isCheckout ? <WhatsAppFab number={settings?.whatsapp_number} /> : null}
    </div>
  );
}
